import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';

import Alert from '../../../../../../components/global/Alert';
import Button from '../../../../../../components/global/Button';
import FormGroup from '../../../../../../components/global/FormGroup';
import Label from '../../../../../../components/global/Label';
import PhoneInput from '../../../../../../components/global/PhoneInput';
import Spinner from '../../../../../../components/global/Spinner';
import { RosterIdentityForContactInfoEdits } from '../../../../../../graphql/queries/roster-identitiy';
import { getRosterIdentityContactMethods } from '../../../../../../util/identity';
import { isBlankPhone, isValidPhone } from '../../../../../../util/phone';
import { isValidEmail } from '../../../../../../util/email';
import uuidv4 from '../../../../../../util/uuid';
import useRosterIdentityContactMethodUpdate from './hooks/useRosterIdentityContactMethodUpdate';
import { CONTACT_METHOD_LOCKED_COPY, EMAIL_ERROR_KEY } from './constants';
import ContactMethodRow from './components/ContactMethodRow';
import EmailRow from './components/EmailRow';

const propTypes = {
  rosterIdentityId: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
  onComplete: PropTypes.func,
};

const RosterIdentityLinkedIdentityContactInfo = ({
  rosterIdentityId,
  onCancel,
  onComplete,
}) => {
  // Values the PRO has typed. An absent entry means untouched, which is what
  // keeps unchanged rows out of the submitted change set.
  const [phoneEdits, setPhoneEdits] = useState({});
  const [addedPhones, setAddedPhones] = useState([]);
  const [emailEdits, setEmailEdits] = useState({});
  const [removedEmailIds, setRemovedEmailIds] = useState([]);
  const [addedEmails, setAddedEmails] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const { data: { me } = {}, loading } = useQuery(
    RosterIdentityForContactInfoEdits,
    {
      variables: { roster_identity_ids: [rosterIdentityId] },
      // The member form runs this same query to decide whether to show the
      // entry point, so render from what it already loaded and revalidate.
      fetchPolicy: 'cache-and-network',
    },
  );

  const [rosterIdentity] = me?.roster_identities?.records ?? [];

  const { phoneRows, emailRows, accountEmailRows } =
    getRosterIdentityContactMethods(rosterIdentity);

  const { updating, rowErrors, onContactMethodsUpdate } =
    useRosterIdentityContactMethodUpdate({ rosterIdentityId });

  const visibleEmailRows = emailRows.filter(
    ({ id }) => !removedEmailIds.includes(id),
  );

  // Two different explanations. The banner speaks to methods someone has
  // authenticated - a locked account, a consumer's own address, or one they
  // have since claimed. An address that is merely unreachable, because no
  // marketing account grants access to it, gets its own note instead.
  const hasAuthenticatedMethod =
    phoneRows.some(({ canUpdate }) => !canUpdate) ||
    accountEmailRows.length > 0 ||
    emailRows.some(({ claimed }) => claimed);

  const hasUnmanageableEmail = emailRows.some(
    ({ canUpdate, claimed }) => !canUpdate && !claimed,
  );

  // PhoneInput reports a value on mount, so an entry in phoneEdits is not by
  // itself a change. Everything below compares against the stored value.
  const getPhoneChanges = () =>
    phoneRows
      .map(row => {
        const phone = phoneEdits[row.linkedIdentityId];

        if (!row.canUpdate || phone === undefined || phone === row.phone) {
          return null;
        }

        return {
          linkedIdentityId: row.linkedIdentityId,
          phoneNumber: phone,
        };
      })
      .filter(Boolean);

  const getPhoneCreations = () =>
    addedPhones
      .filter(({ value }) => !isBlankPhone(value))
      .map(({ key, value }) => ({ key, phoneNumber: value }));

  // Editing or removing an existing address needs the marketing account that
  // grants access to it, and that the address is not one a consumer has since
  // claimed. Adding a new one needs neither, so a member with no marketing
  // account can still be given a first address here.
  const getEmailChanges = () => ({
    updated: visibleEmailRows
      .filter(({ canUpdate }) => canUpdate)
      .map(row => {
        const email = emailEdits[row.id]?.trim();

        if (email === undefined || email === row.emailAddress) {
          return null;
        }

        return { id: row.id, emailAddress: email };
      })
      .filter(Boolean),
    created: addedEmails.map(({ value }) => value.trim()).filter(Boolean),
    deleted: removedEmailIds.filter(
      id => emailRows.find(row => row.id === id)?.canUpdate,
    ),
  });

  const hasEmailChanges = emailChanges =>
    emailChanges.updated.length > 0 ||
    emailChanges.created.length > 0 ||
    emailChanges.deleted.length > 0;

  const invalidPhoneMessage =
    'Provided phone number is not valid. Please provide a valid one.';

  const validate = (phoneChanges, emailChanges) => {
    const errors = {};

    phoneChanges.forEach(({ linkedIdentityId, phoneNumber }) => {
      // A bare country code reads as "no number", matching the main form.
      if (isBlankPhone(phoneNumber)) {
        return;
      }

      if (!isValidPhone(phoneNumber)) {
        errors[linkedIdentityId] = invalidPhoneMessage;
      }
    });

    // An added row that was never filled in is dropped rather than flagged,
    // but anything typed into one has to be a real number.
    addedPhones.forEach(({ key, value }) => {
      if (!isBlankPhone(value) && !isValidPhone(value)) {
        errors[key] = invalidPhoneMessage;
      }
    });

    emailChanges.updated.forEach(({ id, emailAddress }) => {
      if (!isValidEmail(emailAddress, true)) {
        errors[id] = 'Please provide a valid email address.';
      }
    });

    // As with phones, a row that was never filled in is dropped rather than
    // flagged - only what the PRO actually typed has to be valid.
    addedEmails.forEach(({ key, value }) => {
      if (!value.trim()) {
        return;
      }

      if (!isValidEmail(value.trim(), true)) {
        errors[key] = 'Please provide a valid email address.';
      }
    });

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const onSave = async () => {
    const phoneChanges = getPhoneChanges();
    const phoneCreations = getPhoneCreations();
    const emailChanges = getEmailChanges();

    if (!validate(phoneChanges, emailChanges)) {
      return;
    }

    const { success, errors } = await onContactMethodsUpdate({
      phoneChanges,
      phoneCreations,
      emailChanges,
    });

    // Anything that landed is now a real row, so its local copy has to go or
    // the refetch renders the same method a second time. Rows that failed stay
    // put, with what the PRO typed still in them.
    setAddedPhones(addedPhones.filter(({ key }) => errors[key]));

    if (!errors[EMAIL_ERROR_KEY]) {
      setAddedEmails([]);
      setRemovedEmailIds([]);
      setEmailEdits({});
    }

    if (success) {
      onComplete?.();
    }
  };

  const pendingEmailChanges = getEmailChanges();
  const hasPendingChanges =
    getPhoneChanges().length > 0 ||
    getPhoneCreations().length > 0 ||
    hasEmailChanges(pendingEmailChanges);

  // cache-and-network keeps reporting a load while it revalidates, so only
  // block on the first fetch, when there is nothing to render yet.
  if (loading && !rosterIdentity) {
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  }

  const renderError = key =>
    validationErrors[key] || rowErrors[key] ? (
      <p className="text-danger text-xs">
        {validationErrors[key] || rowErrors[key]}
      </p>
    ) : null;

  return (
    <Fragment>
      <h4 className="text-center mb-2">Contact methods</h4>

      {hasAuthenticatedMethod && (
        <Alert type="info">{CONTACT_METHOD_LOCKED_COPY}</Alert>
      )}

      {phoneRows.length === 0 &&
        emailRows.length === 0 &&
        accountEmailRows.length === 0 &&
        addedPhones.length === 0 &&
        addedEmails.length === 0 && (
          <p className="text-center text-light">
            This member has no contact methods yet.
          </p>
        )}

      {/*
        One heading for the whole group, as with the addresses below. Each
        input carries its own accessible name instead, since a single label
        cannot name more than one of them.
      */}
      <div className="mb-2" data-testid="contact-method-phones">
        {(phoneRows.length > 0 || addedPhones.length > 0) && (
          <Label id="contact-method-phones-label">Mobile Phone Number</Label>
        )}

        {phoneRows.map(row => (
          <ContactMethodRow
            key={row.linkedIdentityId}
            testId={`contact-method-phone-${row.linkedIdentityId}`}
            error={renderError(row.linkedIdentityId)}
          >
            <PhoneInput
              id={`phone-${row.linkedIdentityId}`}
              aria-label="Mobile Phone Number"
              value={phoneEdits[row.linkedIdentityId] ?? row.phone ?? ''}
              disabled={!row.canUpdate || updating}
              onChange={phone =>
                setPhoneEdits({
                  ...phoneEdits,
                  [row.linkedIdentityId]: phone,
                })
              }
            />
          </ContactMethodRow>
        ))}

        {addedPhones.map(({ key, value }) => (
          <ContactMethodRow
            key={key}
            removable
            disabled={updating}
            removeLabel="Remove phone number"
            error={renderError(key)}
            onRemove={() =>
              setAddedPhones(addedPhones.filter(added => added.key !== key))
            }
          >
            <PhoneInput
              id={`phone-new-${key}`}
              aria-label="Mobile Phone Number"
              value={value}
              disabled={updating}
              onChange={phone =>
                setAddedPhones(
                  addedPhones.map(added =>
                    added.key === key ? { ...added, value: phone } : added,
                  ),
                )
              }
            />
          </ContactMethodRow>
        ))}

        <Button
          btnStyle="link"
          btnSize="sm"
          className="p-0 d-block"
          disabled={updating}
          onClick={() =>
            setAddedPhones([...addedPhones, { key: uuidv4(), value: '' }])
          }
        >
          Add phone number
        </Button>
      </div>

      {/*
        Always rendered: adding an address is allowed even when the existing
        ones are read only, so the control has to stay reachable.
      */}
      <div className="mb-2" data-testid="contact-method-emails">
        {(visibleEmailRows.length > 0 ||
          accountEmailRows.length > 0 ||
          addedEmails.length > 0) && (
          <Label id="contact-method-emails-label">Email</Label>
        )}

        {/*
          The consumer's own address rather than the roster identity's, so it
          is shown for reference and can be neither edited nor removed.
        */}
        {accountEmailRows.map(row => (
          <ContactMethodRow
            key={row.linkedIdentityId}
            testId={`contact-method-account-email-${row.linkedIdentityId}`}
          >
            <FormGroup
              id={`account-email-${row.linkedIdentityId}`}
              aria-label="Email"
              value={row.emailAddress}
              disabled
              onChange={() => {}}
            />
          </ContactMethodRow>
        ))}

        {visibleEmailRows.map(row => (
          <EmailRow
            key={row.id}
            inputId={`email-${row.id}`}
            value={emailEdits[row.id] ?? row.emailAddress ?? ''}
            disabled={!row.canUpdate || updating}
            removable={row.canUpdate}
            error={renderError(row.id)}
            onChange={value =>
              setEmailEdits({ ...emailEdits, [row.id]: value })
            }
            onRemove={() => setRemovedEmailIds([...removedEmailIds, row.id])}
          />
        ))}

        {addedEmails.map(({ key, value }) => (
          <EmailRow
            key={key}
            inputId={`email-new-${key}`}
            placeholder="Enter an email address..."
            value={value}
            disabled={updating}
            removable
            error={renderError(key)}
            onChange={newValue =>
              setAddedEmails(
                addedEmails.map(added =>
                  added.key === key ? { ...added, value: newValue } : added,
                ),
              )
            }
            onRemove={() =>
              setAddedEmails(addedEmails.filter(added => added.key !== key))
            }
          />
        ))}

        <Button
          btnStyle="link"
          btnSize="sm"
          className="p-0 d-block"
          disabled={updating}
          onClick={() =>
            setAddedEmails([...addedEmails, { key: uuidv4(), value: '' }])
          }
        >
          Add email address
        </Button>

        {renderError(EMAIL_ERROR_KEY)}
      </div>

      {/*
        Changing an existing address needs a marketing account to grant access
        to it. Adding a new one is always allowed, so this only speaks to the
        addresses already on the member.
      */}
      {hasUnmanageableEmail && (
        <p className="text-light text-xs">
          Existing email addresses for this member cannot be edited here.
        </p>
      )}

      <Button
        btnBlock
        disabled={!hasPendingChanges || updating}
        onClick={onSave}
      >
        {updating ? 'Saving...' : 'Save'}
      </Button>

      <Button
        btnStyle="link"
        className="text-center mt-2"
        btnBlock
        onClick={() => onCancel?.()}
      >
        Cancel
      </Button>
    </Fragment>
  );
};

RosterIdentityLinkedIdentityContactInfo.propTypes = propTypes;

export default RosterIdentityLinkedIdentityContactInfo;
